import {
  clearCortexSelection,
  setCortexSelection,
} from "@via/utils/cortex-selection";

interface RefObject<T> {
  current: T;
}

interface CortexSelectionMeta {
  language: string;
  filePath: string;
}

interface MonacoEditorLike {
  getModel?: () => {
    getValueInRange: (range: unknown) => string | undefined;
  } | null;
  getSelection?: () => unknown;
  onDidChangeCursorSelection?: (
    listener: () => void
  ) => { dispose?: () => void } | null;
  onDidFocusEditorText?: (
    listener: () => void
  ) => { dispose?: () => void } | null;
  onDidBlurEditorText?: (
    listener: () => void
  ) => { dispose?: () => void } | null;
  hasTextFocus?: () => boolean;
}

export function attachCortexSelectionTracking(params: {
  editor: MonacoEditorLike;
  selectionMetaRef: RefObject<CortexSelectionMeta>;
  selectionDisposableRef: RefObject<{ dispose?: () => void } | null>;
  lastSelectedTextRef: RefObject<string>;
  maxChars?: number;
}): void {
  const {
    editor,
    selectionMetaRef,
    selectionDisposableRef,
    lastSelectedTextRef,
    maxChars = 16000,
  } = params;

  selectionDisposableRef.current?.dispose?.();

  let isEditorTextFocused = editor.hasTextFocus?.() ?? true;

  function getSelectedText(): string {
    const model = editor.getModel?.();
    const selection = editor.getSelection?.();
    if (!model || !selection) return "";
    const text = model.getValueInRange(selection) ?? "";
    return typeof text === "string" ? text.trim() : "";
  }

  function commitSelection(): void {
    const nextText = getSelectedText();
    if (!nextText) {
      // If user deselects *within the editor*, clear selection (matches old withAI feel).
      // If editor lost focus (e.g. user clicked into Cortex drawer), keep the last selection.
      if (isEditorTextFocused) {
        if (lastSelectedTextRef.current) lastSelectedTextRef.current = "";
        clearCortexSelection();
      }
      return;
    }

    if (nextText === lastSelectedTextRef.current) return;
    lastSelectedTextRef.current = nextText;

    const truncated =
      nextText.length > maxChars ? nextText.slice(0, maxChars) : nextText;
    const meta = selectionMetaRef.current;

    setCortexSelection({
      text: truncated,
      language: meta.language,
      filePath: meta.filePath,
    });
  }

  const focusDisposable =
    editor.onDidFocusEditorText?.(() => {
      isEditorTextFocused = true;
    }) ?? null;
  const blurDisposable =
    editor.onDidBlurEditorText?.(() => {
      isEditorTextFocused = false;
    }) ?? null;

  commitSelection();
  const selectionDisposable =
    editor.onDidChangeCursorSelection?.(() => {
      commitSelection();
    }) ?? null;

  selectionDisposableRef.current = {
    dispose: () => {
      selectionDisposable?.dispose?.();
      focusDisposable?.dispose?.();
      blurDisposable?.dispose?.();
    },
  };
}
